import { cn } from "@/lib/cn";

export type BrandTone =
  /** Default: the full tile - deep plate, teal ring, near-white check. Light surfaces. */
  | "accent"
  /** Deep panel and hero band: no plate, drawn straight onto the rail. */
  | "panel"
  /** The accent-filled auth panel: monochrome, no plate. */
  | "inverse";

const MARK_TONES: Record<BrandTone, { plate: string | null; ring: string; check: string }> = {
  accent: { plate: "text-panel", ring: "text-panel-accent", check: "text-panel-ink" },
  panel: { plate: null, ring: "text-panel-accent", check: "text-panel-ink" },
  inverse: { plate: null, ring: "text-accent-ink", check: "text-accent-ink" },
};

const WORD_TONES: Record<BrandTone, { task: string; iq: string }> = {
  accent: { task: "text-ink", iq: "text-accent" },
  panel: { task: "text-panel-ink", iq: "text-panel-accent" },
  inverse: { task: "text-accent-ink", iq: "text-accent-ink/70" },
};

export function TaskIqMark({
  className,
  size = "md",
  tone = "accent",
}: {
  className?: string;
  size?: LogoSize;
  tone?: BrandTone;
}) {
  const { plate, ring, check } = MARK_TONES[tone];

  return (
    <svg
      viewBox="0 0 1095 1095"
      aria-hidden
      focusable="false"
      fill="none"
      className={cn(
        "brand-mark",
        `brand-mark--${size}`,
        plate === null && "brand-mark--bare",
        className
      )}
    >
      {/* rx is 28% of the side, the same proportion the 32px mark used. Anything
          tighter and the corners are the first thing to go at a 20px rail. */}
      {plate && <rect width="1095" height="1095" rx="308" className={plate} fill="currentColor" />}
      <path className={ring} fill="currentColor" fillRule="evenodd" d={RING} />
      <path className={check} fill="currentColor" fillRule="evenodd" d={CHECK} />
    </svg>
  );
}

/* Geometry, verbatim from public/logo.svg. Split out of the JSX only because a
   3kb `d` attribute inline makes the component unreadable - they are the same
   two paths, in the same order, and must be replaced together when the artwork
   changes. */

const RING =
  "M801.5 733.9C798.47 732.46 796.39 729.86 794.01 727.5C789.99 723.51 786 719.5 782.03 715.47C772.77 706.07 763.5 696.66 754.1 687.4C750.91 684.25 747.8 681.05 744.64 677.87C741.99 675.2 738.83 672.78 737.03 669.5C737.1 668.54 736.97 667.64 737.25 666.69C737.97 664.18 745.46 654.93 747.42 651.92C754.62 640.89 761.44 629.33 766.69 617.24C781.9 582.19 789.4 546.81 788.07 508.5C787.76 499.5 787.23 490.45 785.77 481.55C778.13 434.79 756.6 385.46 723.87 350.65C678.91 302.82 613.12 274.78 547.5 274.91C487.3 275.02 428.24 297.81 383.57 338.08C331.02 385.45 301.27 458.03 303.94 528.5C305.24 562.76 313.94 595.93 328.64 626.84C347.12 665.67 376.48 696.59 411.35 721.08C431.89 735.49 457.42 747.33 481.68 753.69C520.03 763.74 560.15 764.65 598.72 755.35C604.3 754 609.88 752.67 615.3 750.75C631.16 745.14 646.64 738.89 661.53 731.01C665.84 728.72 670.13 726.33 674.32 723.83C676.28 722.66 678.43 720.76 680.69 720.22C682.09 719.89 683.11 720.13 684.5 720.22C690.91 725.16 696.37 731.68 702.09 737.4C711.42 746.74 720.64 756.2 730.03 765.47C734.73 770.11 739.33 774.85 744.02 779.49C747.81 783.23 752.25 786.98 754.63 791.83C758.43 799.61 759.59 809.01 761.27 817.43C764.14 831.76 767.21 846.07 770.29 860.37C773.01 872.97 775.71 885.61 778.41 898.22C779.49 903.32 782.75 911.05 780.5 915.8C768.67 921.51 752.44 906.84 742.89 900.58C722.02 886.88 701.46 872.7 680.64 858.91C674.63 854.93 668.62 850.95 662.65 846.9C658.75 844.25 654.93 841.17 650.5 839.58C646.64 840.15 643.04 841.57 639.29 842.71C632.65 844.72 626.02 846.96 619.3 848.68C603.59 852.69 587.39 855.09 571.24 856.54C560.71 857.49 550.06 857.48 539.5 857.27C474.97 856.04 412.16 836.04 358.63 799.93C281.46 747.86 229.09 666.07 213.2 574.46C208.16 545.46 207.75 515.79 210.05 486.5C212.91 449.93 223.14 414.49 237.35 380.82C248.41 354.63 263.42 329.02 281.47 306.99C304.66 278.68 330.9 253.16 361.66 233.12C396.57 210.38 436.02 193.98 476.79 185.41C508.06 178.83 540.63 176.34 572.5 179.14C613.73 182.76 652.47 193.3 690.1 210.42C701.48 215.6 712.34 221.99 723 228.49C800.81 275.95 856.07 357.4 873.7 446.63C889.21 525.15 879.78 601.26 844.53 673.03C836.37 689.64 826.26 705.75 814.92 720.38C812.81 723.11 806.65 732.27 803.95 733.45C803.11 733.82 802.38 733.74 801.5 733.9ZM860.73 788.39C867.33 787.41 874.38 787.83 880.82 789.58C903.69 795.82 919.69 818.84 917.73 842.49C916.34 859.16 906.46 874.19 892.25 882.73C886.97 885.89 880.6 888.07 874.5 888.86C867.49 889.77 860.16 889.24 853.42 887.13C831.1 880.16 816.18 857.86 817.96 834.5C818.74 824.17 823.17 814.12 829.52 806.04C833.17 801.39 837.98 797.47 843.09 794.57C848.72 791.38 854.32 789.35 860.73 788.39Z";

const CHECK =
  "M500.5 558.08C505.61 555.15 509.77 549.64 513.92 545.43C522.02 537.21 529.93 528.73 538.21 520.7C568.32 491.51 596.91 460.73 626.87 431.38C632.61 425.76 640.83 415.79 647.25 411.73C662.36 402.15 682.01 407.03 692.02 421.49C694.13 424.53 695.82 427.99 696.83 431.55C703.17 453.98 686.18 466.1 672.44 479.94C649.7 502.86 626.75 525.6 604.15 548.65C586.83 566.31 569.62 584.14 551.95 601.45C543.54 609.69 535.2 618.05 527 626.49C519.16 634.56 511.96 642.9 499.5 642.15C484.99 641.28 475.1 627.65 465.53 617.96C449.63 601.86 433.61 585.88 417.7 569.78C413.24 565.26 408.75 560.75 404.28 556.22C402.13 554.04 399.7 552.16 397.89 549.65C390.68 539.64 389.05 526.2 394.52 515C402.77 498.12 423.75 490.34 440.45 500.03C447.01 503.83 453.24 511.46 458.57 516.93C467.47 526.03 476.64 534.85 485.52 543.97C489.88 548.44 495.25 554.98 500.5 558.08Z";

export type LogoSize = "sm" | "md" | "lg";

export function TaskIqLogo({
  className,
  tone = "accent",
  size = "md",
  /** Mark only - for a collapsed rail, where the wordmark has nowhere to go. */
  markOnly = false,
}: {
  className?: string;
  tone?: BrandTone;
  size?: LogoSize;
  markOnly?: boolean;
}) {
  const word = WORD_TONES[tone];

  return (
    <span className={cn("brand", `brand--${size}`, className)}>
      <TaskIqMark tone={tone} size={size} />
      {!markOnly && (
        // One accessible name for the pair, so a screen reader hears "TaskIQ"
        // rather than "task I Q" split across two differently-styled spans.
        <span className="brand__word" aria-label="TaskIQ">
          <span aria-hidden className={cn("brand__task", word.task)}>
            Task
          </span>
          <span aria-hidden className={cn("brand__task", word.iq)}>
            IQ
          </span>
        </span>
      )}
    </span>
  );
}

/** "by Visionify" endorsement line, for the marketing footer and auth screens. */
export function VisionifyByline({
  className,
  tone = "accent",
}: {
  className?: string;
  tone?: BrandTone;
}) {
  return (
    <span
      className={cn(
        "text-sm",
        tone === "accent" ? "text-ink-subtle" : "text-panel-ink-subtle",
        className
      )}
    >
      by{" "}
      <span
        className={cn("brand__byline", tone === "accent" ? "text-ink-muted" : "text-panel-ink")}
      >
        Visionify
      </span>
    </span>
  );
}
